import type { IntlShape } from 'react-intl';

import { getColor } from './get-color.js';
import { getTooltipFormatter } from './get-tooltip-formatter.js';
import type { SeriesIdToColorScale } from '../../../context/XYChartColorScales.context.js';
import type {
  AreaSeriesContent,
  BarSeriesContent,
  DotSeriesContent,
  LineSeriesContent,
  RectSeriesContent,
} from '../../../types/state.js';
import type {
  AxisIdToFormatter,
  BarMetadata,
  DotMetadata,
  LineMetadata,
  RectMetadata,
} from '../../../types/xy-chart-internals.js';
import type {
  AreaTooltipPayload,
  BarTooltipPayload,
  DotTooltipPayload,
  LineTooltipPayload,
  RectTooltipPayload,
  XYChartTooltipPayload,
} from '../../../types/xy-chart.js';
import {
  isAreaSeries,
  isBarSeries,
  isDotSeries,
  isRectSeries,
} from '../../../utils/type-guards.js';

export const getCustomTooltipPayload = (
  content:
    | RectSeriesContent
    | DotSeriesContent
    | LineSeriesContent
    | BarSeriesContent
    | AreaSeriesContent,
  metadata: RectMetadata | DotMetadata | LineMetadata | BarMetadata,
  axisIdToFormatter: AxisIdToFormatter,
  intl: IntlShape,
  colorScaleMap: SeriesIdToColorScale,
  colorOverride?: string,
): XYChartTooltipPayload => {
  const baseTooltipPayload = {
    datapoint: content.datapoint,
    color: getColor(
      content.series,
      content.datapoint,
      metadata,
      colorScaleMap,
      colorOverride,
    ),
    seriesId: content.series.id,
    seriesName: metadata.name,
    xFormatter: getTooltipFormatter(metadata.xAxis, axisIdToFormatter, intl),
    yFormatter: getTooltipFormatter(metadata.yAxis, axisIdToFormatter, intl),
    xAxisLabel: content.series.xAxisId,
    yAxisLabel: content.series.yAxisId,
  };

  if (isRectSeries(content.series)) {
    const rectMetadata = metadata as RectMetadata;

    return {
      rectPayload: {
        ...baseTooltipPayload,
        datapoint: {
          ...baseTooltipPayload.datapoint,
          value: content.datapoint.value,
        },
        valueLabel:
          rectMetadata.valueAccessorLabel ?? rectMetadata.valueAccessor ?? '',
        valueFormatter: rectMetadata.valueFormatter,
      } as RectTooltipPayload,
    };
  } else if (isDotSeries(content.series)) {
    const dotMetadata = metadata as DotMetadata;

    return {
      dotPayload: {
        ...baseTooltipPayload,
        shape: dotMetadata.shape,
      } as DotTooltipPayload,
    };
  } else if (isBarSeries(content.series)) {
    return {
      barPayload: {
        ...baseTooltipPayload,
      } as BarTooltipPayload,
    };
  } else if (isAreaSeries(content.series)) {
    return {
      areaPayload: {
        ...baseTooltipPayload,
      } as AreaTooltipPayload,
    };
  } else {
    return {
      linePayload: {
        ...baseTooltipPayload,
      } as LineTooltipPayload,
    };
  }
};
